import axios from "axios";
import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import qs from "qs";
import { GRAY_VERSION, SHIP_GRAY_VERSION } from "@zhihaoscm/mo-config";
import type { ApiResponse } from "./interface/apiResponse";

export interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
    /** 是否跳过请求状态回调，供下载和后台静默请求使用。 */
    loading?: boolean;
    /** 是否保留 Axios 原始响应，不解包业务 data。 */
    returnRawResponse?: boolean;
    /** 是否跳过文件请求的取消器，兼容旧文件服务配置。 */
    cancel?: boolean;
}

export interface RequestHooks {
    /** 从 React/Zustand 用户状态读取当前 token。 */
    getToken?: () => string;
    /** 由路由层决定登录失效后的跳转。 */
    onUnauthorized?: (response: AxiosResponse<ApiResponse<unknown>>) => void;
    /** 由提示层决定业务错误展示方式。 */
    onError?: (error: unknown) => void;
    /** 由页面层决定是否展示全屏或局部 loading。 */
    onRequestStateChange?: (loading: boolean, config: CustomAxiosRequestConfig) => void;
}

/** 将响应中的业务错误转为保留 code/message/data 的可捕获对象。 */
const createApiError = <T>(response: ApiResponse<T>, status?: number) =>
    Object.assign(new Error(response.message || "请求失败"), response, { status });

/** 统一判断旧 Java API 的成功响应编码，兼容字符串和数字 0。 */
const isSuccessCode = (code: ApiResponse<unknown>["code"] | undefined) =>
    code === undefined || code === null || code === "0" || code === 0;

/** React 迁移期间复用 Vue API 契约的 Axios 薄适配器。 */
export class RequestHttp {
    public readonly service: AxiosInstance;

    public constructor(
        config: AxiosRequestConfig,
        private readonly hooks: RequestHooks = {}
    ) {
        this.service = axios.create({
            timeout: 30000,
            paramsSerializer: params => qs.stringify(params, { arrayFormat: "repeat" }),
            ...config
        });

        this.service.interceptors.request.use(
            (requestConfig: InternalAxiosRequestConfig) => {
                const customConfig = requestConfig as CustomAxiosRequestConfig;
                if (customConfig.loading !== false) {
                    this.hooks.onRequestStateChange?.(true, customConfig);
                }

                const token = this.hooks.getToken?.();
                if (token) {
                    requestConfig.headers.set("Authorization", token);
                }
                requestConfig.headers.set(
                    "Version",
                    requestConfig.headers.get("X-Request-From") === "shipping" ? SHIP_GRAY_VERSION : GRAY_VERSION
                );
                requestConfig.headers.set("app-type", "1");
                requestConfig.headers.set("X-platform", "admin");
                return requestConfig;
            },
            error => Promise.reject(error)
        );

        this.service.interceptors.response.use(
            <T>(response: AxiosResponse<ApiResponse<T>, CustomAxiosRequestConfig>) => {
                const customConfig = response.config as CustomAxiosRequestConfig;
                if (customConfig.loading !== false) {
                    this.hooks.onRequestStateChange?.(false, customConfig);
                }
                if (customConfig.returnRawResponse) {
                    return response;
                }

                const payload = response.data;
                if (response.status === 401) {
                    this.hooks.onUnauthorized?.(response as AxiosResponse<ApiResponse<unknown>>);
                    const error = createApiError(payload, response.status);
                    this.hooks.onError?.(error);
                    return Promise.reject(error);
                }
                if (!isSuccessCode(payload.code)) {
                    const error = createApiError(payload, response.status);
                    this.hooks.onError?.(error);
                    return Promise.reject(error);
                }
                return payload as unknown as AxiosResponse<ApiResponse<T>>;
            },
            (error: AxiosError<ApiResponse<unknown>>) => {
                const customConfig = error.config as CustomAxiosRequestConfig | undefined;
                if (customConfig && customConfig.loading !== false) {
                    this.hooks.onRequestStateChange?.(false, customConfig);
                }

                const response = error.response;
                if (response?.status === 401 || response?.data?.code === "401" || response?.data?.code === 401) {
                    this.hooks.onUnauthorized?.(response);
                }

                const apiError = response?.data ? createApiError(response.data, response.status) : error;
                this.hooks.onError?.(apiError);
                return Promise.reject(apiError);
            }
        );
    }
}
